/**
 * Simple Sample Calculator
 * Handles both 7.5" and 15" sample types in one component
 */
class SimpleSampleCalculator {
  constructor() {
    this.elements = this.initializeElements();
    this.state = {
      currentQuantity: 1,
      maxQuantity: 3,
      sampleType: '7.5',
      currentPrice: 0,
      isLoading: false
    };

    if (this.elements.calculator) {
      this.bindEvents();
      this.initialize();
      this.setupVariantChangeListeners();
    }
  }

  /**
   * Initialize DOM elements
   */
  initializeElements() {
    return {
      calculator: document.querySelector('.simple-sample-calculator'),
      title: document.getElementById('sample-title'),
      description: document.getElementById('sample-description'),
      price: document.getElementById('sample-price'),
      quantityDisplay: document.getElementById('sample-quantity'),
      minusBtn: document.querySelector('.minus-btn'),
      plusBtn: document.querySelector('.plus-btn'),
      addToCartBtn: document.getElementById('simple-sample-add-to-cart'),
      limitMessage: document.getElementById('sample-limit-message'),
      variantAttributes: document.querySelector('.variant_attributes'),
      variantRadios: document.querySelectorAll('#variant-options input[type="radio"]'),
      selectedProductId: document.querySelector('form[action="/cart/add"] input[name="id"]')
    };
  }

  /**
   * Setup variant change listeners
   */
  setupVariantChangeListeners() {
    // Listen for radio button changes in div#variant-options
    this.elements.variantRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          setTimeout(() => {
            this.updateConfiguration();
          }, 50);
        }
      });
    });

    // Use MutationObserver to watch for programmatic changes to radio buttons
    // (when the main controller updates them based on URL changes)
    const variantOptionsContainer = document.getElementById('variant-options');
    if (variantOptionsContainer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'checked') {
            const target = mutation.target;
            if (target.type === 'radio' && target.checked) {
              setTimeout(() => {
                this.updateConfiguration();
              }, 50);
            }
          }
        });
      });
      
      // Observe all radio buttons for checked attribute changes
      this.elements.variantRadios.forEach(radio => {
        observer.observe(radio, {
          attributes: true,
          attributeFilter: ['checked']
        });
      });
    }

    // Also listen for changes to the main product form variant ID as backup
    if (this.elements.selectedProductId) {
      const formObserver = new MutationObserver(() => {
        setTimeout(() => {
          this.updateConfiguration();
        }, 50);
      });
      
      formObserver.observe(this.elements.selectedProductId, {
        attributes: true,
        attributeFilter: ['value']
      });
    }
  }

  /**
   * Initialize calculator
   */
  initialize() {
    this.detectSampleType();
    this.updateDisplay();
    this.updateButtonStates();
    this.updateShopifyQuantity();
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    const { minusBtn, plusBtn, addToCartBtn } = this.elements;

    if (minusBtn) {
      minusBtn.addEventListener('click', () => this.decreaseQuantity());
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', () => this.increaseQuantity());
    }

    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => this.handleAddToCart());
    }
  }

  /**
   * Detect sample type from current variant
   */
  detectSampleType() {
    // Get current variant title from various sources
    const variantTitle = this.getCurrentVariantTitle();
    
    if (this.is7_5Sample(variantTitle)) {
      this.configureSample('7.5', 3, '7.5" Single Sample', 'An easy, affordable way to see real wood texture and tone, great for quick comparisons around your home.');
    } else if (this.is15Sample(variantTitle)) {
      this.configureSample('15', 1, '15" Sample 3-Pack', 'Three longer planks to show full color range and grain, ideal for comparing against paint, cabinets, and furniture.');
    } else {
      // Default to 7.5" for generic samples
      this.configureSample('7.5', 3, '7.5" Single Sample', 'An easy, affordable way to see real wood texture and tone, great for quick comparisons around your home.');
    }
  }

  /**
   * Configure sample type
   */
  configureSample(sampleType, maxQuantity, title, description) {
    this.state.sampleType = sampleType;
    this.state.maxQuantity = maxQuantity;
    
    // Reset quantity if it exceeds new max
    if (this.state.currentQuantity > maxQuantity) {
      this.state.currentQuantity = 1;
    }
    
    // Update title and description
    if (this.elements.title) {
      this.elements.title.textContent = title;
    }
    
    if (this.elements.description) {
      this.elements.description.textContent = description;
    }
  }

  /**
   * Get current variant title from various sources
   */
  getCurrentVariantTitle() {
    // Try to get from checked radio first
    const checkedRadio = document.querySelector('#variant-options input[type="radio"]:checked');
    if (checkedRadio) {
      return checkedRadio.getAttribute('data-title');
    }
    
    // Try to get from selected variant in form
    const selectedVariantInput = document.querySelector('form[action="/cart/add"] input[name="id"]');
    if (selectedVariantInput) {
      const variantId = selectedVariantInput.value;
      const allRadios = document.querySelectorAll('#variant-options input[type="radio"]');
      for (const radio of allRadios) {
        if (radio.value === variantId) {
          return radio.getAttribute('data-title');
        }
      }
    }
    
    return '';
  }

  /**
   * Check if variant is a 7.5" sample
   */
  is7_5Sample(variantTitle) {
    if (!variantTitle) return false;
    
    const patterns = [
      /^7\.5$/i,                // Just "7.5"
      /^7\.5"$/i,               // "7.5""
      /7\.5.*sample/i,
      /7\.5.*plank/i,
      /7\.5\&quot;.*sample/i,
      /7\.5".*sample/i,
      /7\.5'.*sample/i,
      /7½.*sample/i,
      /seven.*five.*sample/i,
      /7-5.*sample/i,
      /single.*7\.5.*sample/i,
      /1.*-.*7\.5.*sample/i,
      /1.*7\.5.*sample/i,
      /\b7\.5\b/i               // Any "7.5" word boundary
    ];
    
    return patterns.some(pattern => pattern.test(variantTitle));
  }

  /**
   * Check if variant is a 15" sample
   */
  is15Sample(variantTitle) {
    if (!variantTitle) return false;
    
    const patterns = [
      /^15$/i,                  // Just "15"
      /^15"$/i,                 // "15""
      /^3\s*-\s*15$/i,         // "3 - 15" format
      /^3.*15$/i,              // "3...15" variations
      /15.*sample/i,
      /15.*samples/i,
      /15.*plank/i,
      /15.*planks/i,
      /15\&quot;.*sample/i,
      /15".*sample/i,
      /15'.*sample/i,
      /3.*-.*15.*sample/i,
      /3.*15.*sample/i,
      /three.*15.*sample/i,
      /\b15\b/i                 // Any "15" word boundary
    ];
    
    return patterns.some(pattern => pattern.test(variantTitle));
  }

  /**
   * Check if variant is a sample type
   */
  isSampleVariant(variantTitle) {
    if (!variantTitle) return false;
    return /\bsample\b/i.test(variantTitle);
  }

  /**
   * Update display
   */
  updateDisplay() {
    const { quantityDisplay } = this.elements;
    const { currentQuantity } = this.state;

    // Update price from current variant
    this.updatePrice();

    // Update quantity display
    if (quantityDisplay) {
      quantityDisplay.textContent = currentQuantity;
    }
  }

  /**
   * Update price from current variant
   */
  updatePrice() {
    const { price } = this.elements;
    
    // Try multiple methods to get current variant price
    let variantPrice = 0;
    let variantId = null;
    
    // Method 1: Get from checked radio button
    const checkedRadio = document.querySelector('#variant-options input[type="radio"]:checked');
    if (checkedRadio) {
      variantId = checkedRadio.value;
      variantPrice = parseFloat(checkedRadio.getAttribute('data-price')) || 0;
    }
    
    // Method 2: Get from main product form if no checked radio
    if (!variantPrice) {
      const selectedVariantInput = document.querySelector('form[action="/cart/add"] input[name="id"]');
      if (selectedVariantInput) {
        variantId = selectedVariantInput.value;
        const allRadios = document.querySelectorAll('#variant-options input[type="radio"]');
        
        for (const radio of allRadios) {
          if (radio.value === variantId) {
            variantPrice = parseFloat(radio.getAttribute('data-price')) || 0;
            break;
          }
        }
      }
    }
    
    // Update state and display
    this.state.currentPrice = variantPrice;
    
    if (price && variantPrice > 0) {
      const totalPrice = (variantPrice / 100) * this.state.currentQuantity;
      price.textContent = this.formatPrice(totalPrice);
    }
  }

  /**
   * Update button states
   */
  updateButtonStates() {
    const { minusBtn, plusBtn } = this.elements;
    const { currentQuantity, maxQuantity } = this.state;

    if (minusBtn) {
      minusBtn.disabled = currentQuantity <= 1;
    }

    if (plusBtn) {
      plusBtn.disabled = currentQuantity >= maxQuantity;
    }
  }

  /**
   * Increase quantity
   */
  increaseQuantity() {
    const { maxQuantity, currentQuantity } = this.state;

    if (currentQuantity < maxQuantity) {
      this.state.currentQuantity++;
      this.updateDisplay();
      this.updateButtonStates();
      this.updateShopifyQuantity();
      this.hideLimitMessage();
    } else {
      this.showLimitMessage();
    }
  }

  /**
   * Decrease quantity
   */
  decreaseQuantity() {
    const { currentQuantity } = this.state;

    if (currentQuantity > 1) {
      this.state.currentQuantity--;
      this.updateDisplay();
      this.updateButtonStates();
      this.updateShopifyQuantity();
      this.hideLimitMessage();
    }
  }

  /**
   * Show limit message
   */
  showLimitMessage() {
    const { limitMessage } = this.elements;
    const { maxQuantity, sampleType } = this.state;

    if (limitMessage) {
      let message = '';
      if (sampleType === '7.5') {
        message = `Sorry, limit ${maxQuantity} samples per product.`;
      } else if (sampleType === '15') {
        message = `Sorry, limit ${maxQuantity} sample 3-pack per product.`;
      } else {
        message = `Sorry, limit ${maxQuantity} sample${maxQuantity > 1 ? 's' : ''} per product.`;
      }
      
      limitMessage.textContent = message;
      limitMessage.style.display = 'block';
      
      setTimeout(() => {
        this.hideLimitMessage();
      }, 3000);
    }
  }

  /**
   * Hide limit message
   */
  hideLimitMessage() {
    const { limitMessage } = this.elements;

    if (limitMessage) {
      limitMessage.style.display = 'none';
    }
  }

  /**
   * Update Shopify form quantity
   */
  updateShopifyQuantity() {
    const shopifyQuantityInput = document.querySelector('form[action="/cart/add"] input[name="quantity"]');
    if (shopifyQuantityInput) {
      shopifyQuantityInput.value = this.state.currentQuantity;
    }

    const customQuantityInput = document.querySelector('.custom-quantity__input');
    if (customQuantityInput) {
      customQuantityInput.value = this.state.currentQuantity;
    }
  }

  /**
   * Handle add to cart
   */
  async handleAddToCart() {
    const { addToCartBtn } = this.elements;
    const { currentQuantity, isLoading } = this.state;

    if (isLoading || currentQuantity <= 0) return;

    const variantIdInput = document.querySelector('form[action="/cart/add"] input[name="id"]');
    if (!variantIdInput || !variantIdInput.value) {
      this.showToast('Please select a product variant', 'error');
      return;
    }

    const variantId = variantIdInput.value;

    this.setLoadingState(true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: currentQuantity
          }]
        })
      });

      if (response.ok) {
        this.setLoadingState(false);
        this.setSuccessState();
        this.showToast('Sample added to cart successfully!', 'success');
        
        // Update cart count if available
        this.updateCartCount();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add to cart');
      }
    } catch (error) {
      this.showToast('Error adding to cart: ' + error.message, 'error');
      this.setLoadingState(false);
    }
  }

  /**
   * Show toast notification
   */
  showToast(message, type = 'info') {
    // Remove existing toast
    const existingToast = document.querySelector('.sample-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create toast element
    const toast = document.createElement('div');
    toast.className = `sample-toast sample-toast--${type}`;
    toast.innerHTML = `
      <div class="sample-toast__content">
        <span class="sample-toast__icon">${type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}</span>
        <span class="sample-toast__message">${message}</span>
      </div>
      <button class="sample-toast__close" aria-label="Close notification">×</button>
    `;

    // Add to page
    document.body.appendChild(toast);

    // Add event listener for close button
    const closeBtn = toast.querySelector('.sample-toast__close');
    closeBtn.addEventListener('click', () => {
      toast.classList.add('sample-toast--hiding');
      setTimeout(() => toast.remove(), 300);
    });

    // Auto remove after 5 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.classList.add('sample-toast--hiding');
        setTimeout(() => toast.remove(), 300);
      }
    }, 5000);

    // Trigger animation
    setTimeout(() => toast.classList.add('sample-toast--visible'), 100);
  }

  /**
   * Update cart count
   */
  updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
        });
      })
      .catch(error => {
        console.warn('Could not update cart count:', error);
      });
  }

  /**
   * Set loading state
   */
  setLoadingState(loading) {
    const { addToCartBtn } = this.elements;
    
    this.state.isLoading = loading;

    if (addToCartBtn) {
      addToCartBtn.disabled = loading;
      
      if (loading) {
        addToCartBtn.innerHTML = `
          <span class="btn-spinner"></span>
          <span class="btn-text">ADDING TO CART</span>
        `;
        addToCartBtn.classList.add('loading');
      } else {
        addToCartBtn.innerHTML = 'ADD TO CART';
        addToCartBtn.classList.remove('loading');
      }
    }
  }

  /**
   * Set success state
   */
  setSuccessState() {
    const { addToCartBtn } = this.elements;
    
    if (addToCartBtn) {
      addToCartBtn.innerHTML = `
        <span class="btn-checkmark">✓</span>
        <span class="btn-text">ADDED TO CART</span>
      `;
      addToCartBtn.classList.add('success');
      
      // Reset after 2 seconds
      setTimeout(() => {
        addToCartBtn.innerHTML = 'ADD TO CART';
        addToCartBtn.classList.remove('success');
        addToCartBtn.disabled = false;
      }, 2000);
    }
  }

  /**
   * Format price
   */
  formatPrice(price) {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
    
    // Remove leading zero for prices under $1.00 (e.g., $0.99 becomes $.99)
    return formatted.replace(/^\$0\./, '$.');
  }

  /**
   * Show calculator
   */
  show() {
    if (this.elements.calculator) {
      this.elements.calculator.style.display = 'block';
      this.detectSampleType();
      this.updateDisplay();
      this.updateButtonStates();
    }
  }

  /**
   * Hide calculator
   */
  hide() {
    if (this.elements.calculator) {
      this.elements.calculator.style.display = 'none';
    }
  }

  /**
   * Update configuration when variant changes
   */
  updateConfiguration() {
    // Reset quantity to 1 when variant changes
    this.state.currentQuantity = 1;
    
    // Detect new sample type and update configuration
    this.detectSampleType();
    
    // Update all display elements
    this.updateDisplay();
    this.updateButtonStates();
    this.updateShopifyQuantity();
    
    // Hide any limit messages
    this.hideLimitMessage();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  window.simpleSampleCalculator = new SimpleSampleCalculator();
  window.SimpleSampleCalculator = SimpleSampleCalculator;
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SimpleSampleCalculator;
} 